import { useState, useCallback, useEffect, useMemo } from 'react';

import type { ImportedTransactions } from '../../hooks';

import { Modal } from '@simplywallst/ui-core';

import { useUploadTransactions } from '../../hooks';
import { useCompanyBatchSearch } from './useCompanyBatchSearch';

import { DefaultState } from './DefaultState';
import { SuccessState } from './SuccessState';
import { ErrorState } from './ErrorState';

import { useTracking } from 'react-tracking';

import { useUser } from '@/hooks/useUser';

import { getMatchedListingsMap } from './utils';

import { isCurrencyISO } from '@/constants/currencies';
import { isCountryISO, countries } from '@/constants/countries';

const getCurrencyISO = (countryISO: string) => {
  const formattedCountryISO = countryISO.toLowerCase();
  if (isCountryISO(formattedCountryISO)) {
    const currencyISO = countries[formattedCountryISO]?.currencyISO;
    return isCurrencyISO(currencyISO) ? currencyISO : null;
  }
  return null;
};

type Props = {
  populatedRowCount: number;
  incompleteRowCount: number;
  onClose: () => void;
  onSuccess: (response: ImportedTransactions) => void;
};

export const CSVImportModal = ({
  populatedRowCount,
  incompleteRowCount,
  onClose,
  onSuccess,
}: Props) => {
  const { data: user } = useUser();
  const { trackEvent } = useTracking();

  const [selectedFileName, setSelectedFileName] = useState<string>('');

  const [importedTransactions, setImportedTransactions] =
    useState<ImportedTransactions>([]);

  const validQueries = useMemo(() => {
    return importedTransactions
      .reduce<string[]>((a, b) => {
        if (b.ticker !== null) {
          if (!a.includes(b.ticker)) {
            a.push(b.ticker);
          }
        }
        return a;
      }, [])
      .filter((q): q is string => q !== null);
  }, [importedTransactions]);

  const { data, status: searchStatus } = useCompanyBatchSearch(validQueries);

  const {
    mutate,
    status: importStatus,
    reset,
  } = useUploadTransactions({
    onSuccess: async (data) => {
      if (!data) return;
      setImportedTransactions(data);
    },
  });

  useEffect(() => {
    if (typeof data === 'undefined') return;

    const matchedListingsMap = getMatchedListingsMap(
      data,
      user.defaultMarketISO
    );

    onSuccess(
      importedTransactions.map((transaction) => {
        const { ticker } = transaction;

        if (ticker === null) return transaction;

        // We use the ticker it was used as the query to search for the company
        const listing = matchedListingsMap[ticker];

        if (listing === null) return transaction;

        const [exchangeSymbol, tickerSymbol] = listing.uniqueSymbol.split(':');

        return {
          ...transaction,
          company: {
            exchangeSymbol,
            tickerSymbol,
            name: listing.name,
            data: {
              currencyInfo: {
                reporting_currency_iso: getCurrencyISO(
                  listing.exchangeCountryIso
                ),
              },
            },
          },
        };
      })
    );
  }, [data, user, importedTransactions, onSuccess]);

  const handleImport = useCallback(
    (file: File) => {
      setImportedTransactions([]);
      setSelectedFileName(file.name);
      mutate(file);
    },
    [mutate]
  );

  const handleReset = useCallback(() => {
    setSelectedFileName('');

    trackEvent({
      modifier: 'uploadTransactionsModal',
      action: 'reset',
    });

    reset();
  }, [reset, trackEvent]);

  const handleClose = useCallback(() => {
    setSelectedFileName('');

    trackEvent({
      modifier: 'uploadTransactionsModal',
      action: 'close',
    });

    onClose();
  }, [onClose, trackEvent]);

  const isSuccess = importStatus === 'success' && searchStatus === 'success';
  const isError = importStatus === 'error' || searchStatus === 'error';

  return (
    <Modal isNaked fullscreen>
      <div
        className="grid grid-cols-[auto] content-center md:grid-cols-[700px]"
        data-theme="light"
      >
        {isError ? (
          <ErrorState
            selectedFileName={selectedFileName}
            onClose={handleClose}
            onReset={handleReset}
          />
        ) : isSuccess ? (
          <SuccessState
            selectedFileName={selectedFileName}
            populatedRowCount={populatedRowCount}
            incompleteRowCount={incompleteRowCount}
            onClose={handleClose}
          />
        ) : (
          <DefaultState
            onImport={handleImport}
            onClose={handleClose}
            importStatus={importStatus} // TODO: remove this prop
            selectedFileName={selectedFileName}
            populatedRowCount={populatedRowCount}
          />
        )}
      </div>
    </Modal>
  );
};
